import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { baseGet, basePost } from '@qsrsoft/myq-core';
import { useCatalogOverridesStore } from '@/stores/catalogOverrides';

const APP_NAME = 'reportOverrides';

// Same storage as catalog overrides: the organization settings endpoint under
// a synthetic `enterprise-<client>-<country>` org key, so defaults scope to
// the (enterprise, country) tuple. One document per enterprise key, one
// setting per report id:
//
//   {
//     'reports-mtSalesDetail': {
//       filters: { 'dateData.datePreset': 'Yesterday', 'locationData.locationAgg': 's' },
//       chart: { defaultMetric: 'transactions' },
//       grids: { mcSalesDetail: ['allNetSales', 'transactions'] },
//     },
//   }
function buildRoute(scopeKey) {
  return `organization/enterprise-${scopeKey}/settings?appName=${APP_NAME}`;
}

function parseDocument(data) {
  let doc = data;
  if (typeof doc === 'string') {
    try {
      doc = JSON.parse(doc);
    } catch {
      return {};
    }
  }
  return doc && typeof doc === 'object' && !Array.isArray(doc) ? doc : {};
}

function isPlainObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

// Drop empty values so a cleared field disappears rather than persisting as
// '' / null, and so an all-empty report resets to "no override".
export function pruneOverride(entry) {
  const out = {};
  const filters = {};
  for (const [path, value] of Object.entries(entry?.filters || {})) {
    if (value === '' || value === null || value === undefined) continue;
    filters[path] = value;
  }
  if (Object.keys(filters).length) out.filters = filters;
  const metric = entry?.chart?.defaultMetric;
  if (typeof metric === 'string' && metric.trim()) {
    out.chart = { defaultMetric: metric };
  }
  const grids = {};
  for (const [settingsName, cols] of Object.entries(entry?.grids || {})) {
    if (Array.isArray(cols) && cols.length) grids[settingsName] = [...cols];
  }
  if (Object.keys(grids).length) out.grids = grids;
  return out;
}

export const useReportOverridesStore = defineStore('reportOverrides', () => {
  const catalogStore = useCatalogOverridesStore();
  const { enterpriseKey } = storeToRefs(catalogStore);

  const overridesByReport = ref({});
  const loaded = ref(false);
  const documentKey = ref(null);
  let documentPromise = null;

  // True once the document for the *current* enterprise key is in memory.
  // `loaded` alone is not enough: main.js may call load() before the root
  // store knows the enterprise, and a later load() with the real key must
  // not look "ready" until it resolves.
  const ready = computed(
    () => loaded.value && !!documentKey.value && documentKey.value === enterpriseKey.value,
  );

  function getFor(reportId) {
    return overridesByReport.value[reportId] || null;
  }

  // Whole-document fetch, shared by every report and by the settings store's
  // grid-column lookup. Cached per enterprise key; a failure is treated as
  // "no overrides" and still marks the document loaded so views don't stall.
  function load() {
    const key = enterpriseKey.value;
    if (!key) return Promise.resolve({});
    if (documentPromise && documentKey.value === key) return documentPromise;
    documentKey.value = key;
    loaded.value = false;
    documentPromise = (async () => {
      try {
        const doc = parseDocument(await baseGet(buildRoute(key)));
        const entries = {};
        for (const [reportId, value] of Object.entries(doc)) {
          if (isPlainObject(value)) entries[reportId] = pruneOverride(value);
        }
        overridesByReport.value = { ...entries, ...overridesByReport.value };
        return overridesByReport.value;
      } catch (err) {
        console.warn('[reportOverrides] load failed', err);
        return {};
      } finally {
        loaded.value = true;
      }
    })();
    return documentPromise;
  }

  async function save(reportId, entry) {
    const key = enterpriseKey.value;
    if (!key) throw new Error('No enterpriseClient available; cannot save report overrides');
    const cleaned = pruneOverride(entry);
    const route = `${buildRoute(key)}&settingName=${encodeURIComponent(reportId)}`;
    if (Object.keys(cleaned).length) {
      await basePost(route, cleaned);
      overridesByReport.value = { ...overridesByReport.value, [reportId]: cleaned };
    } else {
      // Settings API delete-by-null convention: drop the row entirely.
      await basePost(route, null);
      const next = { ...overridesByReport.value };
      delete next[reportId];
      overridesByReport.value = next;
    }
    return getFor(reportId);
  }

  function reset(reportId) {
    return save(reportId, {});
  }

  // settingsName → report id, for the grid-column lookup. Built lazily from
  // the loaded document so it needs no registry import here.
  const gridIndex = computed(() => {
    const idx = {};
    for (const [reportId, entry] of Object.entries(overridesByReport.value)) {
      for (const settingsName of Object.keys(entry?.grids || {})) {
        idx[settingsName] = reportId;
      }
    }
    return idx;
  });

  // Enterprise default columns for a grid, or null. Awaits the document so the
  // settings store can call it before anything else has loaded overrides.
  async function gridColumnsFor(settingsName) {
    if (!settingsName) return null;
    await load();
    const reportId = gridIndex.value[settingsName];
    const cols = reportId ? getFor(reportId)?.grids?.[settingsName] : null;
    return Array.isArray(cols) && cols.length ? [...cols] : null;
  }

  return {
    overridesByReport,
    loaded,
    ready,
    enterpriseKey,
    load,
    save,
    reset,
    getFor,
    gridColumnsFor,
  };
});
